/**
 * Context Warning Banner
 *
 * Proactive warning banner that appears when chat approaches context limit.
 * Shows at 80% capacity with clear call-to-action.
 */

import { AlertTriangle, MessageSquarePlus, X } from 'lucide-react';
import React from 'react';
import { cn } from '../utils/cn';
import { formatTokenCount, OPENAI_LIMITS } from '../utils/token-estimation';
import { Button } from './button';
import { IconButton } from './icon-button';

export type ContextWarningBannerProps = {
  estimatedTokens: number;
  onStartNewChat: () => void;
  onDismiss?: () => void;
  className?: string;
};

export const ContextWarningBanner: React.FC<ContextWarningBannerProps> = ({
  estimatedTokens,
  onStartNewChat,
  onDismiss,
  className,
}) => {
  const percentage = estimatedTokens / OPENAI_LIMITS.GPT4_VISION_CONTEXT;
  const tokensRemaining = OPENAI_LIMITS.GPT4_VISION_CONTEXT - estimatedTokens;

  // Determine severity
  const isCritical = percentage >= OPENAI_LIMITS.CRITICAL_THRESHOLD;
  const isWarning = percentage >= OPENAI_LIMITS.WARNING_THRESHOLD;

  if (!isWarning) return null;

  return (
    <div
      className={cn(
        'rounded-lg border p-4',
        'animate-in slide-in-from-top-2 duration-300',
        isCritical
          ? 'bg-orange-50 dark:bg-orange-900/10 border-orange-200 dark:border-orange-800'
          : 'bg-yellow-50 dark:bg-yellow-900/10 border-yellow-200 dark:border-yellow-800',
        className
      )}
    >
      <div className="flex items-start gap-3">
        {/* Icon */}
        <div
          className={cn(
            'w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0',
            isCritical
              ? 'bg-orange-100 dark:bg-orange-900/30'
              : 'bg-yellow-100 dark:bg-yellow-900/30'
          )}
        >
          <AlertTriangle
            className={cn(
              'w-4 h-4',
              isCritical
                ? 'text-orange-600 dark:text-orange-400'
                : 'text-yellow-600 dark:text-yellow-400'
            )}
          />
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0">
          <h3
            className={cn(
              'text-sm font-semibold mb-1',
              isCritical
                ? 'text-orange-900 dark:text-orange-100'
                : 'text-yellow-900 dark:text-yellow-100'
            )}
          >
            {isCritical ? 'Chat Almost Full' : 'Chat Getting Long'}
          </h3>
          <p
            className={cn(
              'text-xs leading-relaxed mb-3',
              isCritical
                ? 'text-orange-700 dark:text-orange-300'
                : 'text-yellow-700 dark:text-yellow-300'
            )}
          >
            {isCritical ? (
              <>
                Your conversation is at <strong>{Math.round(percentage * 100)}%</strong> capacity (
                <strong>{formatTokenCount(tokensRemaining)}</strong> tokens remaining).
                <br />
                Consider starting a new chat to ensure full AI capabilities.
              </>
            ) : (
              <>
                You've used <strong>{Math.round(percentage * 100)}%</strong> of the context window (
                <strong>{formatTokenCount(tokensRemaining)}</strong> tokens remaining).
                <br />
                Starting fresh soon will prevent reaching the limit.
              </>
            )}
          </p>

          <Button
            size="sm"
            variant={isCritical ? 'default' : 'outline'}
            onClick={onStartNewChat}
            className="gap-2"
          >
            <MessageSquarePlus className="w-3.5 h-3.5" />
            Start Fresh Chat
          </Button>
        </div>

        {/* Dismiss button */}
        {onDismiss && (
          <IconButton
            iconName="X"
            variant="ghost"
            size="sm"
            onClick={onDismiss}
            className={cn(
              'flex-shrink-0',
              isCritical
                ? 'text-orange-600 hover:text-orange-700 dark:text-orange-400'
                : 'text-yellow-600 hover:text-yellow-700 dark:text-yellow-400'
            )}
            aria-label="Dismiss warning"
          />
        )}
      </div>
    </div>
  );
};

ContextWarningBanner.displayName = 'ContextWarningBanner';
